import { type ReactElement, type ReactNode, useMemo } from 'react';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiContext } from '../lib/api/api-context.js';
import { API_BASE_URL, type ApiClient, createApiClient } from '../lib/api/client.js';
import { createQueryClient } from '../lib/api/query-client.js';
import { AuthProvider } from '../lib/auth/auth-provider.js';
import type { AuthSnapshot } from '../lib/auth/auth-context.js';
import { emitSessionExpired } from '../lib/auth/session-expiry.js';
import type { ConnectivityStore } from '../lib/offline/connectivity.js';
import { OfflineProvider } from '../lib/offline/offline-provider.js';
import type { WriteQueue } from '../lib/offline/write-queue.js';
import { SessionContext, type ShopSession } from '../lib/session/session-context.js';
import {
  ColumnPickerPreferencesProvider,
  ConfirmProvider,
  ToastProvider,
  type ColumnPickerPreferences,
} from '@tailoring-crm/ui';
import {
  useResetTableColumns,
  useSaveTableColumns,
  useTableColumns,
} from '../lib/table-preferences/use-table-preferences.js';

/**
 * What `ColumnPicker` (in @tailoring-crm/ui) reads and writes a person's column preference
 * through. A module constant, so the context value never changes identity between renders.
 */
const columnPickerPreferences: ColumnPickerPreferences = {
  useTableColumns,
  useSaveTableColumns,
  useResetTableColumns,
};

/**
 * Every cross-cutting provider, in the one order that works:
 *
 *   QueryClient → ApiClient → Auth (needs both) → Offline → Toast → Confirm → Session
 *
 * All of them are injectable so a test can mount a real screen against a stub fetch, an
 * in-memory queue, a fake connection and a chosen session without touching globals.
 *
 * `SessionContext` is left here as an explicit override for the handful of tests that render a
 * layout component in isolation. In the running application it is `AuthenticatedShell` that
 * fills it, from the real session — so the header's shop name comes from the server rather
 * than from whatever the root happened to be handed.
 */
export interface AppProvidersProps {
  children: ReactNode;
  client?: ApiClient;
  queryClient?: QueryClient;
  session?: ShopSession | null;
  /**
   * Skips the auth provider's boot request and starts signed in. Tests only; the application
   * always asks the server who is signed in.
   */
  initialAuth?: AuthSnapshot | null;
  /** Called when a 401 could not be recovered by refreshing. Defaults to the auth expiry bus. */
  onSessionExpired?: () => void;
  /** Test seams — a fake connection and an in-memory queue. */
  connectivity?: ConnectivityStore;
  queue?: WriteQueue;
}

export function AppProviders({
  children,
  client,
  queryClient,
  session = null,
  initialAuth,
  onSessionExpired,
  connectivity,
  queue,
}: AppProvidersProps): ReactElement {
  const { t } = useTranslation();

  const resolvedQueryClient = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);

  const resolvedClient = useMemo(
    () =>
      client ??
      createApiClient({
        baseUrl: API_BASE_URL,
        unexpectedErrorMessage: t('error.unexpected'),
        // A 401 the client could not refresh away reaches the auth provider, which asks for
        // the password over whatever screen is open rather than navigating away from it.
        onSessionExpired: onSessionExpired ?? emitSessionExpired,
      }),
    [client, onSessionExpired, t],
  );

  return (
    <QueryClientProvider client={resolvedQueryClient}>
      <ApiContext.Provider value={resolvedClient}>
        <AuthProvider {...(initialAuth === undefined ? {} : { initial: initialAuth })}>
          <OfflineProvider
            client={resolvedClient}
            {...(connectivity ? { connectivity } : {})}
            {...(queue ? { queue } : {})}
          >
            <ToastProvider>
              <ConfirmProvider>
                <ColumnPickerPreferencesProvider preferences={columnPickerPreferences}>
                  <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
                </ColumnPickerPreferencesProvider>
              </ConfirmProvider>
            </ToastProvider>
          </OfflineProvider>
        </AuthProvider>
      </ApiContext.Provider>
    </QueryClientProvider>
  );
}
